import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FeatureViews } from "src/app/ui/models/domains/features/custom-feature-views";
import { PageViews } from "src/app/ui/models/domains/features/custom-page-views";
import { ClusterProduct } from "src/app/ui/models/domains/pbm/clusters/cluster-product.model";
import { ClusterSearchParameters } from "src/app/ui/models/domains/pbm/clusters/cluster-search-parameters.model";
import { Cluster } from "src/app/ui/models/domains/pbm/clusters/cluster.model";
import { FileExportProcessor } from "src/app/ui/models/domains/pbm/exports/file-export-processor";
import { RoutePath } from "src/app/ui/models/domains/route-paths.model";
import { FeatureViewService } from "src/app/ui/services/feature-view.service";
import { FileExportService } from "src/app/ui/services/file-export.service";
import { LoadingService } from "src/app/ui/services/loading.service";
import { ClustersService } from "src/app/ui/services/pbm/clusters.service";
import { Toast } from "../../../toast/toast";
import { ConfirmDialog } from "../../shared/dialogs/confirm-dialog/confirm-dialog.component";
import { SearchClusterExportOrderRequest } from "../search-cluster-management/search-cluster-export-order-request";
import { AddBatchProductDialogComponent } from "./add-batch-product-dialog/add-batch-product-dialog.component";
import { AddProductDialogComponent } from "./add-product-dialog/add-product-dialog.component";
import { MatDialogRef, MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";

@Component({
  selector: "edit-cluster",
  templateUrl: "./edit-cluster.component.html",
  styleUrls: ["./edit-cluster.component.scss"],
})
export class EditClusterComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;

  ean: string;
  displayedColumns: string[] = ["ean", "productName", "deleteProduct"];
  clusterId: string;
  dialogValue: string;
  totalCount: number;
  pageIndex: number;
  cluster: Cluster = new Cluster(this.activatedRoute.snapshot.params.clusterKey, "", 0, "", "", []);
  dataSource = new MatTableDataSource<ClusterProduct>();
  confirmDialogRef: MatDialogRef<ConfirmDialog>;
  addBatchProductDialogRef: MatDialogRef<AddBatchProductDialogComponent>;
  addProductDialogRef: MatDialogRef<AddProductDialogComponent>;
  public fileExportProcessor = new FileExportProcessor();

  constructor(
    private activatedRoute: ActivatedRoute,
    private clustersService: ClustersService,
    private loadingService: LoadingService,
    private toast: Toast,
    private dialog: MatDialog,
    private router: Router,
    private featureViewService: FeatureViewService,
    private fileExportService: FileExportService,
  ) {}

  async ngOnInit() {
    this.featureViewService.activeFeatureView(FeatureViews.EditClusters, PageViews.ClustersManagementPageView);
  }

  async ngAfterViewInit() {
    const clusterKey = this.activatedRoute.snapshot.params.clusterKey;
    this.dataSource = new MatTableDataSource<ClusterProduct>(this.cluster.getClusterProducts());
    await this.getCluster();
    await this.getProductList(clusterKey, "");
  }

  public hasClusterProducts(): boolean {
    return this.dataSource.data.length > 0;
  }

  public async getCluster() {
    const clusterKey = this.activatedRoute.snapshot.params.clusterKey;
    this.cluster = await this.clustersService.getCluster(clusterKey);
  }

  public changePage($event) {
    const clusterKey = this.activatedRoute.snapshot.params.clusterKey;
    this.pageIndex = $event.pageIndex + 1;
    this.getProductList(clusterKey, "");
  }

  public async getProductList(clusterKey: string, filterText: string): Promise<void> {
    const loadignRef = this.loadingService.beginLoading();
    const clusterSearchParameters = new ClusterSearchParameters();

    clusterSearchParameters.setIdCluster(clusterKey);
    clusterSearchParameters.setFilterText(filterText);
    clusterSearchParameters.setStartIndex(this.pageIndex * this.paginator.pageSize - this.paginator.pageSize || 0);
    clusterSearchParameters.setTotalPerPages(this.paginator.pageSize);
    try {
      const ret = await this.clustersService.getClusterProducts(clusterKey, clusterSearchParameters);
      this.dataSource.data = ret.getData();
      this.totalCount = ret.total;
    } catch {
      this.toast.showWarningToast("Houve um problema ao carregar os dados do cluster. Tente novamente mais tarde.");
    } finally {
      loadignRef.closeLoading();
    }
  }

  public addBatchProduct(): void {
    this.clusterId = this.activatedRoute.snapshot.params.clusterKey;
    this.addBatchProductDialogRef = this.dialog.open(AddBatchProductDialogComponent, {
      disableClose: true,
      hasBackdrop: true,
      width: "60vw",
      data: { clusterId: this.clusterId },
    });

    this.addBatchProductDialogRef.componentInstance.addBatchClusterProductDialogEvent.subscribe((result) => {
      this.getProductList(this.clusterId, "");
    });
  }

  public addProduct(): void {
    const clusterKey = this.activatedRoute.snapshot.params.clusterKey;

    this.addProductDialogRef = this.dialog.open(AddProductDialogComponent, {
      disableClose: true,
      hasBackdrop: true,
      width: "60vw",
      data: clusterKey,
    });

    this.addProductDialogRef.componentInstance.addClusterProductDialogEvent.subscribe(async () => {
      const clusterKey = this.activatedRoute.snapshot.params.clusterKey;
      await this.getProductList(clusterKey, "");
    });
  }

  public async searchProductsByEan(): Promise<any> {
    this.pageIndex = 1;
    if (!!this.ean && this.ean !== null) {
      const clusterKey = this.activatedRoute.snapshot.params.clusterKey;
      await this.getProductList(clusterKey, this.ean);
    } else {
      const clusterKey = this.activatedRoute.snapshot.params.clusterKey;
      await this.getProductList(clusterKey, "");
    }
  }

  public async exportClusterList(cluster: Cluster): Promise<any> {
    const ref = this.loadingService.beginLoading();
    const clusterFileExportOrderRequest = new SearchClusterExportOrderRequest(this.clustersService, this.toast, this.fileExportService, cluster);

    await this.fileExportProcessor.processFileBySas(clusterFileExportOrderRequest, ref);
  }

  public async deleteProduct(item: string): Promise<void> {
    const clusterKey = this.activatedRoute.snapshot.params.clusterKey;
    const loadignRef = this.loadingService.beginLoading();

    try {
      if (!!item) {
        await this.clustersService.deleteClusterProduct(clusterKey, item);
        this.dataSource._updateChangeSubscription();
      }
    } catch {
      this.toast.showWarningToast("Houve um problema ao deletar o produto, Tente novamente mais tarde.");
    } finally {
      loadignRef.closeLoading();
    }
  }

  public onDeleteProduct(item: string): void {
    const clusterKey = this.activatedRoute.snapshot.params.clusterKey;

    this.confirmDialogRef = this.dialog.open(ConfirmDialog, {
      disableClose: true,
      hasBackdrop: true,
      height: "200px",
      width: "600px",
      data: {
        title: "Remover produto",
        text: `Deseja remover o produto: ${item}?`,
      },
    });

    this.confirmDialogRef.componentInstance.confirmDialogEvent.subscribe(async () => {
      await this.deleteProduct(item);
      await this.getProductList(clusterKey, "");
    });
  }

  public backToClusters(): void {
    this.router.navigate([RoutePath.ClusterManagement]);
  }
}
